import { Link } from '@tanstack/react-router'
import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react'
import type { ReactNode } from 'react'

import type { Customer, CustomerListTotals } from '@/lib/api/customer-api'
import type { CustomerSort } from './customers-list-filters'
import { DebtAmount } from '@/components/customers/debt-ledger-tab'
import { Badge } from '@/components/ui/badge'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { formatQty } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'

const COLUMNS = 7
// "Tổng N khách" trải qua các cột trước cột nợ.
const LEAD_COLUMNS = 4

// Bấm tiêu đề cột nợ xoay vòng như KiotViet: mặc định → nhiều nhất → ít nhất.
const NEXT_SORT: Record<CustomerSort, CustomerSort> = {
    default: 'debt_desc',
    debt_desc: 'debt_asc',
    debt_asc: 'default',
}
/**
 * "Số ngày nợ" chỉ có nghĩa khi khách còn nợ: khách đã hết nợ hoặc trả trước mà
 * còn khoản theo đơn chưa đóng thì hiện gạch, không đọc thành "đang nợ N ngày".
 */
export function debtDaysLabel(c: Customer) {
    return (c.current_debt ?? 0) > 0 && c.debt_days != null
        ? m.customer_debt_days_value({ days: String(c.debt_days) })
        : '—'
}

const ARIA_SORT: Record<CustomerSort, 'none' | 'descending' | 'ascending'> = {
    default: 'none',
    debt_desc: 'descending',
    debt_asc: 'ascending',
}

interface Props {
    customers: Customer[]
    isLoading: boolean
    emptyState: ReactNode
    /** Tổng nợ của CẢ tập khách khớp bộ lọc (meta.totals). */
    totals?: CustomerListTotals
    totalCount?: number
    sort: CustomerSort
    onSortChange: (next: CustomerSort) => void
}

/** Bảng khách desktop với cột nợ sắp xếp được, số ngày nợ và dòng tổng. */
export function CustomersListTable({
    customers,
    isLoading,
    emptyState,
    totals,
    totalCount,
    sort,
    onSortChange,
}: Props) {
    const SortIcon =
        sort === 'debt_desc'
            ? ArrowDown
            : sort === 'debt_asc'
              ? ArrowUp
              : ArrowUpDown
    return (
        <Table>
            <TableHeader>
                <TableRow>
                    <TableHead>{m.customer_code()}</TableHead>
                    <TableHead>{m.customer_name_label()}</TableHead>
                    <TableHead>{m.customer_phone_label()}</TableHead>
                    <TableHead>{m.customer_group()}</TableHead>
                    <TableHead aria-sort={ARIA_SORT[sort]}>
                        <button
                            type="button"
                            aria-label={m.customer_debt_sort_aria()}
                            onClick={() => onSortChange(NEXT_SORT[sort])}
                            className="focus-visible:outline-ring inline-flex items-center gap-1 uppercase focus-visible:outline-2"
                        >
                            {m.customer_debt_col_current()}
                            <SortIcon className="size-3.5" />
                        </button>
                    </TableHead>
                    <TableHead>{m.customer_debt_days_col()}</TableHead>
                    <TableHead>{m.product_status()}</TableHead>
                </TableRow>
            </TableHeader>
            <TableBody>
                {isLoading ? (
                    <TableRow>
                        <TableCell
                            colSpan={COLUMNS}
                            className="text-center text-muted-foreground py-8"
                        >
                            {m.common_loading()}
                        </TableCell>
                    </TableRow>
                ) : customers.length === 0 ? (
                    <TableRow>
                        <TableCell colSpan={COLUMNS}>{emptyState}</TableCell>
                    </TableRow>
                ) : (
                    <>
                        {totals ? (
                            <TableRow className="bg-muted/50 font-semibold hover:bg-muted/50">
                                <TableCell colSpan={LEAD_COLUMNS}>
                                    {m.customer_totals_row({
                                        count: formatQty(
                                            totalCount ?? customers.length,
                                        ),
                                    })}
                                </TableCell>
                                <TableCell>
                                    <DebtAmount value={totals.current_debt} />
                                </TableCell>
                                <TableCell
                                    colSpan={COLUMNS - LEAD_COLUMNS - 1}
                                />
                            </TableRow>
                        ) : null}
                        {customers.map((c) => (
                            <TableRow key={c.id}>
                                <TableCell className="font-mono text-xs">
                                    {c.code}
                                </TableCell>
                                <TableCell>
                                    <Link
                                        to="/customers/$customerId"
                                        params={{ customerId: c.id }}
                                        search={{ tab: 'info' }}
                                        className="font-medium text-primary underline underline-offset-2 decoration-primary/40 hover:decoration-primary"
                                    >
                                        {c.name}
                                    </Link>
                                </TableCell>
                                <TableCell>{c.phone ?? '—'}</TableCell>
                                <TableCell>
                                    {c.customer_group?.name ?? '—'}
                                </TableCell>
                                <TableCell>
                                    <DebtAmount value={c.current_debt ?? 0} />
                                </TableCell>
                                <TableCell className="tabular-nums">
                                    {debtDaysLabel(c)}
                                </TableCell>
                                <TableCell>
                                    <Badge
                                        variant={
                                            c.is_active
                                                ? 'success'
                                                : 'secondary'
                                        }
                                    >
                                        {c.is_active
                                            ? m.customer_active()
                                            : m.customer_inactive()}
                                    </Badge>
                                </TableCell>
                            </TableRow>
                        ))}
                    </>
                )}
            </TableBody>
        </Table>
    )
}
